import { FC } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

interface Props {
    summary: LabTestSummaryTypes;
    selectedStatus?: string;
    onSelectStatus?: (status: string) => void;
}

// colors validated for colour-blind separation between neighbouring slices, used instead of the api colors
const statusColors: Record<LabTestStatusCount['key'], string> = {
    requested: '#eda100',
    scheduled: '#2a78d6',
    collected: '#e87ba4',
    reportReceived: '#1baf7a',
    cancelled: '#e34948',
};

const LabStatusChart: FC<Props> = ({ summary, selectedStatus, onSelectStatus }) => {
    const { totalBookings, period } = summary;
    const statusWise = summary.statusWise || [];
    const colorOf = (item: LabTestStatusCount) => statusColors[item.key] || item.color;
    // badges in the header: the statuses with the highest share, highest first
    const topStatuses = [...statusWise]
        .filter((item) => item.count > 0)
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, 2);
    const activeRows = statusWise.filter((item) => item.key !== 'cancelled');
    const cancelledRows = statusWise.filter((item) => item.key === 'cancelled');
    const slices = statusWise.filter((item) => item.count > 0);

    const renderRow = (item: LabTestStatusCount) => (
        // a div, not a button: global.css forces every button into a single flex row
        <div
            key={item.key}
            role="button"
            tabIndex={0}
            aria-pressed={selectedStatus === item.status}
            onClick={() => onSelectStatus?.(item.status)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectStatus?.(item.status);
                }
            }}
            className={`py-2 cursor-pointer ${selectedStatus === item.status ? '' : 'opacity-60 hover:opacity-100'}`}
        >
            <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: colorOf(item) }} />
                <span className="flex-1 text-[15px] text-[#1f1f1f]">{item.label}</span>
                <span className="w-12 text-right text-[15px] font-semibold text-[#1f1f1f]">{item.count}</span>
                <span className="w-16 text-right text-[15px] text-[#6b6b6b]">{item.percentage.toFixed(1)}%</span>
            </div>
            <div className="ml-6 mr-1 mt-1.5 h-1 rounded-full bg-[#f1f1ef]">
                <div className="h-1 rounded-full" style={{ width: `${item.percentage}%`, backgroundColor: colorOf(item) }} />
            </div>
        </div>
    );

    return (
        <div className="bg-white border border-[#e6e6e3] rounded-2xl p-6">
            <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-[#1f1f1f]">Health test bookings by status</h3>
                    <small className="text-sm text-[#8a8a87]">{period}</small>
                </div>
                {topStatuses.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                        {topStatuses.map((item) => (
                            <span
                                key={item.key}
                                className="flex items-center gap-2 px-3 py-1 rounded-lg text-sm text-[#1f1f1f]"
                                style={{ backgroundColor: `${colorOf(item)}26` }}
                            >
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorOf(item) }} />
                                {Math.round(item.percentage)}% {item.label.toLowerCase()}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            {
                totalBookings > 0 ?
                    <div className="flex flex-col md:flex-row items-center gap-8 mt-4">
                        <div className="relative w-[260px] h-[260px] shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={slices}
                                        dataKey="count"
                                        nameKey="label"
                                        innerRadius={95}
                                        outerRadius={128}
                                        startAngle={90}
                                        endAngle={-270}
                                        stroke="#ffffff"
                                        strokeWidth={3}
                                        isAnimationActive={false}
                                        onClick={(entry: any) => entry?.payload?.status && onSelectStatus?.(entry.payload.status)}
                                    >
                                        {slices.map((item) => (
                                            <Cell
                                                key={item.key}
                                                fill={colorOf(item)}
                                                cursor="pointer"
                                                opacity={!selectedStatus || selectedStatus === item.status ? 1 : 0.45}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value: number, name: string, entry: any) => [`${value} (${entry?.payload?.percentage}%)`, name]} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="text-4xl text-[#1f1f1f]">{totalBookings}</span>
                                <span className="text-sm text-[#52514e]">Total bookings</span>
                            </div>
                        </div>
                        <div className="flex-1 w-full">
                            {activeRows.map(renderRow)}
                            {cancelledRows.length > 0 && <div className="border-t border-[#e6e6e3] my-1" />}
                            {cancelledRows.map(renderRow)}
                        </div>
                    </div>
                    :
                    <p className="text-center text-[#8a8a87] py-12">No health test bookings found for this period</p>
            }
        </div>
    );
}

export default LabStatusChart;
